// filepath: apps/web/src/features/admin/templates/update/TemplateUpdatePage.tsx
//
// FR-UI-ADM-12 AC-2..AC-8 — composition root for the
// template-update diff + resolution view.
//
// Composes:
//   - useTemplateUpdateOffer (queries) — GET /api/templates/{id}/update-offer
//   - ConflictDiffPanel (AC-2) — side-by-side diff
//   - ConflictResolutionTable (AC-3/AC-4) — per-field radios
//   - ApplyUpdateButton (AC-5/AC-7) — submit
//
// State model:
//   - `resolutions`: { [entityType.fieldName]: ConflictResolution }
//   - `selectedMerge`: the field for which the merge preview is open

import React, { useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { ApiError } from "@/definitions/types";
import {
  classifyTemplateUpdateFetchError,
  useTemplateUpdateApply,
  useTemplateUpdateOffer,
} from "./queries";
import { ConflictDiffPanel } from "./ConflictDiffPanel";
import { ConflictResolutionTable } from "./ConflictResolutionTable";
import { ApplyUpdateButton } from "./ApplyUpdateButton";
import type {
  ApplyUpdateRequest,
  ConflictResolution,
  ConflictResolutionChoice,
  FieldClassification,
} from "./types";

export type ResolutionMap = Record<
  string,
  { choice: ConflictResolutionChoice; merged_value?: unknown }
>;

interface TemplateUpdatePageProps {
  templateId: string;
  installed: string;
  target: string;
  /** Click handler for the irreversible-redirect path (AC-8). */
  onRedirect?: (reviewId: string) => void;
}

export function TemplateUpdatePage({
  templateId,
  installed,
  target,
  onRedirect,
}: TemplateUpdatePageProps): React.ReactElement {
  const { t } = useTranslation();
  const offer = useTemplateUpdateOffer(templateId, installed, target);
  const applyMutation = useTemplateUpdateApply(templateId);
  const [resolutions, setResolutions] = useState<ResolutionMap>({});
  const [mergePreviewField, setMergePreviewField] = useState<string | null>(
    null,
  );
  const [applyError, setApplyError] = useState<string | null>(null);

  // ─── render branches ──────────────────────────────────────────────

  if (offer.isLoading) {
    return (
      <div
        className="p-6 text-sm text-muted-foreground"
        data-testid="template-update-loading"
      >
        {t("common.loading")}
      </div>
    );
  }

  if (offer.error) {
    const classified = classifyTemplateUpdateFetchError(
      offer.error as ApiError,
    );
    return (
      <div
        className="m-6 rounded border border-red-300 bg-red-50 p-4 text-sm text-red-700"
        role="alert"
        data-testid={`template-update-${classified.kind}`}
      >
        {classified.message}
      </div>
    );
  }

  const data = offer.data;
  if (!data) {
    return <div data-testid="template-update-empty" />;
  }

  // Flatten the per-field classifications across every diff.
  const fields: FieldClassification[] = data.diffs.flatMap(
    (d) => d.field_classifications ?? [],
  );

  const conflicts = fields.filter(
    (f) =>
      f.classification === "modified_by_tenant" ||
      f.classification === "added_by_tenant",
  );
  const totalConflicts = conflicts.length;
  const resolvedCount = Object.keys(resolutions).filter((k) => {
    const isConflictField = conflicts.some(
      (f) => `${f.entity_type}.${f.field_name}` === k,
    );
    return isConflictField;
  }).length;

  const handleResolve = (
    key: string,
    choice: ConflictResolutionChoice,
    mergedValue?: unknown,
  ) => {
    setResolutions((prev) => ({
      ...prev,
      [key]: { choice, merged_value: mergedValue },
    }));
  };

  const handleApply = async () => {
    setApplyError(null);
    const request: ApplyUpdateRequest = {
      base_version: installed,
      target_version: target,
      resolutions: conflicts.map<ConflictResolution>((c) => {
        const key = `${c.entity_type}.${c.field_name}`;
        const r = resolutions[key];
        return {
          entity_type: c.entity_type,
          field_name: c.field_name,
          choice: r?.choice ?? "keep_tenant",
          merged_value: r?.merged_value,
        };
      }),
    };
    try {
      const resp = await applyMutation.mutateAsync({
        request,
        ifMatch: installed,
      });
      if (resp.review_id && resp.redirect_url) {
        onRedirect?.(resp.review_id);
      }
    } catch (e) {
      setApplyError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="space-y-4 p-6" data-testid="template-update-page">
      <header>
        <h1 className="text-lg font-semibold">
          {t("admin.templates.update.detail.title", undefined, {
            template_id: templateId,
          })}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t("admin.templates.update.detail.installed_version", undefined, {
            version: installed,
          })}
          {" — "}
          {t("admin.templates.update.detail.target_version", undefined, {
            version: target,
          })}
        </p>
        <p className="text-sm text-muted-foreground">
          {t("admin.templates.update.detail.total_conflicts", undefined, {
            count: totalConflicts,
          })}
        </p>
      </header>

      <ConflictDiffPanel diffs={data.diffs} />

      <ConflictResolutionTable
        fields={fields}
        resolutions={resolutions}
        onResolve={handleResolve}
        mergePreviewField={mergePreviewField}
        onOpenMergePreview={(k) => setMergePreviewField(k)}
        onCloseMergePreview={() => setMergePreviewField(null)}
      />

      <div className="flex items-center justify-between">
        <span
          className="text-sm text-muted-foreground"
          data-testid="template-update-counter"
        >
          {t("admin.templates.update.detail.counter", undefined, {
            resolved: resolvedCount,
            total: totalConflicts,
          })}
        </span>
        <ApplyUpdateButton
          disabled={resolvedCount < totalConflicts || applyMutation.isPending}
          pending={applyMutation.isPending}
          onClick={handleApply}
          resolvedCount={resolvedCount}
          totalConflicts={totalConflicts}
        />
      </div>

      {applyError ? (
        <div
          className="rounded border border-red-300 bg-red-50 p-3 text-xs text-red-700"
          role="alert"
          data-testid="template-update-apply-error"
        >
          {applyError}
        </div>
      ) : null}
    </div>
  );
}
